import type {
  ReferralJob,
  ReferralProgramIdentifier,
  ReferralSetup,
  ReferralSetupApiRequest,
  ReferralStage,
} from 'src/types/referral-v2';
import type {
  GenericHeaderItems,
  GenericObject,
} from '@spaced-out/ui-design-system/lib/components/Table';

import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/index';


export const getStatus = (
  t: (arg1: string, arg2: string) => string,
): {
  REFERRED: string;
  CONSIDERED: string;
  UNDER_REVIEW: string;
  HIRED: string;
  PAID: string;
  NOT_CONSIDERED: string;
} => ({
  REFERRED: t('Referred', 'Referred'),
  CONSIDERED: t('Considered', 'Considered'),
  UNDER_REVIEW: t('Under_Review', 'Under Review'),
  HIRED: t('Hired', 'Hired'),
  PAID: t('Paid', 'Paid'),
  NOT_CONSIDERED: t('Not_Considered', 'Not Considered'),
});

export const SELECTED_TEMP_JOB: ReferralJob = {
  id: '',
  title: '',
  branch: '',
};

const PAYOUT_BULK_ACTION_VALUES = [
  'payout_amount',
  'payout_period',
  'referral_validity',
] as const;

export const getBulkActionMenu = (
  t: (arg1: string, arg2: string) => string,
  hidePayoutColumns?: boolean,
): Array<{
  label: string;
  value: string;
  disabled: boolean;
}> => {
  const allItems = [
    {
      label: t('Link_Jobs_to_Program', 'Link Jobs to Program'),
      value: 'link_jobs',
      disabled: false,
    },
    {
      label: t('Bonus_Amount', 'Bonus Amount'),
      value: 'payout_amount',
      disabled: false,
    },
    {
      label: t('Payout_Period', 'Payout Period'),
      value: 'payout_period',
      disabled: false,
    },
    {
      label: t('Referral_Validity', 'Referral Validity'),
      value: 'referral_validity',
      disabled: false,
    },
    {
      label: t('Unlink_Jobs', 'Unlink Jobs'),
      value: 'unlink_jobs',
      disabled: false,
    },
  ];
  if (hidePayoutColumns) {
    return allItems.filter(
      (item) => !PAYOUT_BULK_ACTION_VALUES.includes(item.value as any),
    );
  }
  return allItems;
};

export const getReferralNotification = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  label: string;
  recruiter: string;
  agent: string;
}> => [
  {
    label: t('Referral_Requests', 'Referral Requests'),
    recruiter: 'referral_request_recruiter_notification',
    agent: 'request',
  },
  {
    label: t('Referral_Submitted', 'Referral Submitted'),
    recruiter: 'referral_submission_recruiter_notification',
    agent: 'submission',
  },
  {
    label: t('Referral_Status_Change', 'Referral Status Change'),
    recruiter: 'referral_status_change_recruiter_notification',
    agent: 'status_change',
  },
  {
    label: t('Referral_Withdrawn', 'Referral Withdrawn'),
    recruiter: 'referral_withdrawn_recruiter_notification',
    agent: 'withdrawn',
  },
  {
    label: t('Referral_Payments', 'Referral Payments'),
    recruiter: 'referral_payment_recruiter_notification',
    agent: 'payments',
  },
];

export const getTrackingArchiveOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {label: t('Not_Considered', 'Not Considered'), key: 'Not Considered'},
  {label: t('Duplicate_Record', 'Duplicate Record'), key: 'Duplicate Record'},
  {label: t('Others', 'Others'), key: 'others'},
];

export const getPayoutArchiveOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    label: t('Referral_left_Organization', 'Referral left Organization'),
    key: 'Referral left Organization',
  },
  {
    label: t('Referrer_left_Organization', 'Referrer left Organization'),
    key: 'Referrer left Organization',
  },
  {label: t('Duplicate_Record', 'Duplicate Record'), key: 'Duplicate Record'},
];

export const trackingAndPayoutPageTabs = [
  {key: 'all', label: 'All'},
  {key: 'archived', label: 'Archived'},
] as const;

export const programAndJobTabs = [
  {key: 'programs', label: 'Programs'},
  {key: 'jobs', label: 'Jobs'},
] as const;

export const getMainTabs = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  tabId: string;
  label?: string;
}> => [
  {tabId: '/dashboard', label: t('Dashboard', 'Dashboard')},
  {tabId: '/tracking', label: t('Tracking', 'Tracking')},
  {tabId: '/payouts', label: t('Payouts', 'Payouts')},
  {tabId: '/programs', label: t('Programs', 'Programs')},
  {tabId: '/jobs', label: t('Jobs', 'Jobs')},
  {tabId: '/earnings', label: t('Earnings', 'Earnings')},
  {tabId: '/settings', label: t('Settings', 'Settings')},
];

export const restrictedTabs = ['/payouts', '/programs'] as const;

export const getRadioSelectOption = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {label: t('Use_Email', 'Use Email'), key: 'email'},
  {label: t('Use_Phone_Number', 'Use Phone Number'), key: 'phone'},
  {
    label: t('Use_Email_or_Phone_Number', 'Use Email or Phone Number'),
    key: 'email_or_phone',
  },
];

export const getRadioChoiceOption = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {label: t('Yes', 'Yes'), key: 'true'},
  {label: t('No', 'No'), key: 'false'},
];

export const getRadioTimeSheetLogOption = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {label: t('Days', 'Days'), key: 'days'},
  {label: t('Hours', 'Hours'), key: 'hours'},
];

export const getTimeSheetSyncOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    label: t('Overwrite', 'Overwrite'),
    key: 'overwrite',
  },
  {
    label: t('Addition', 'Addition'),
    key: 'add',
  },
];

export const getMenuOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {label: t('Edit_Program_Details', 'Edit Program Details'), key: 'edit'},
  {label: t('Duplicate_as_New', 'Duplicate as New'), key: 'duplicate'},
  {label: t('Activate_program', 'Activate program'), key: 'archive'},
  {label: t('Deactivate_Program', 'Deactivate Program'), key: 'unarchive'},
  {
    label: t('Copy_Personalised_Link', 'Copy Personalised  Link'),
    key: 'copy_agent_link',
  },
  {label: t('Copy_Agency_Link', 'Copy Agency Link'), key: 'copy_agency_link'},
];

export const DEFAULT_LINE_ATTRIBUTE = {
  lineVariant: 'default',
  lineStyle: 'dashed',
} as const;

export const LOADING_LINE_ATTRIBUTE = {
  lineVariant: 'loading',
  lineStyle: 'solid',
} as const;

export const SUCCESS_LINE_ATTRIBUTE = {
  lineVariant: 'success',
  lineStyle: 'solid',
} as const;

export const SAVE_BTN_TEXTS = {
  SETUP: 'Save & Continue',
  CONFIGURE: 'Save Changes',
} as const;

export const INITIAL_SETUP_STATE: ReferralSetup = {
  name: '',
  brand_id: '',
  welcome_message: '',
  terms: '',
  must_acknowledge_terms: false,
  tnc_type: 'text',
};

export const INITIAL_REFERRAL_SETTINGS = {
  one_time_settings: {
    should_pay_for_different_role: false,
    reward_for_existing_referral: false,
    duplicate_profile_check_method: 'email',
    timesheet_log_type: 'days',
    payout_rule: null,
  },
  bonus_and_payout_settings: {
    default_bonus_amount: null,
    default_bonus_payout_period: 90,
    default_referral_validity: 90,
    payout_currency: 'USD',
  },
} as const;

export const SETUP_PROGRAM_TAB = [
  {
    id: 0,
    key: 'setup',
    label: 'Program Setup',
    description: 'Basic details of the program',
  },
  {
    id: 1,
    key: 'settings',
    label: 'Settings',
    description: 'One time settings',
  },
  {
    id: 2,
    key: 'questionnaire',
    label: 'Form',
    description: 'Questionnaire for referrals',
  },
  {
    id: 3,
    key: 'user_access',
    label: 'User Access',
    description: 'Program & notification access',
  },
] as const;

export const CREATE_MULTIPLE_PROGRAM_TAB = [
  {
    id: 0,
    key: 'setup',
    label: 'Program Setup',
    description: 'Basic details of the program',
  },
  {
    id: 1,
    key: 'questionnaire',
    label: 'Form',
    description: 'Questionnaire for referrals',
  },
  {
    id: 2,
    key: 'user_access',
    label: 'User Access',
    description: 'Program & notification access',
  },
] as const;

export const getSetupProgramJobListTab = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  id: number;
  key: string;
  label: string;
  description: string;
}> => [
  {
    id: 0,
    key: 'setup',
    label: t('Program_Setup', 'Program Setup'),
    description: t(
      'Basic_details_of_the_program',
      'Basic details of the program',
    ),
  },
  {
    id: 1,
    key: 'settings',
    label: t('Settings', 'Settings'),
    description: t('One_time_settings', 'One time settings'),
  },
  {
    id: 2,
    key: 'questionnaire',
    label: t('Form', 'Form'),
    description: t(
      'Questionnaire_for_referrals',
      'Questionnaire for referrals',
    ),
  },
  {
    id: 3,
    key: 'user_access',
    label: t('User_Access', 'User Access'),
    description: t(
      'Program_notification_access',
      'Program & notification access',
    ),
  },
  {
    id: 4,
    key: 'jobs',
    label: t('Jobs', 'Jobs'),
    description: t('Include_a_job_list', 'Include a job list'),
  },
];

export const getCreateJobListProgramTab = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  id: number;
  key: string;
  label: string;
  description: string;
}> => [
  {
    id: 0,
    key: 'setup',
    label: t('Program_Setup', 'Program Setup'),
    description: t(
      'Basic_details_of_the_program',
      'Basic details of the program',
    ),
  },
  {
    id: 1,
    key: 'questionnaire',
    label: t('Form', 'Form'),
    description: t(
      'Questionnaire_for_referrals',
      'Questionnaire for referrals',
    ),
  },
  {
    id: 2,
    key: 'user_access',
    label: t('User_Access', 'User Access'),
    description: t(
      'Program_notification_access',
      'Program & notification access',
    ),
  },
  {
    id: 3,
    key: 'settings',
    label: t('Bonus_Validity_Settings', 'Bonus & Validity Settings'),
    description: t('Set_the_bonus_validity', 'Set the bonus & validity'),
  },
  {
    id: 4,
    key: 'jobs',
    label: t('Jobs', 'Jobs'),
    description: t('Include_a_job_list', 'Include a job list'),
  },
];

export const normalizePayload = (
  data: ReferralSetup,
  referralProgramIdentifier?: ReferralProgramIdentifier,
): ReferralSetupApiRequest => {
  const payload: ReferralSetupApiRequest = {
    stage: 'setup',
    setup_configuration: {
      name: data.name,
      welcome_message: data.welcome_message,
      brand_id: data.brand_id,
      terms: data.terms,
      tnc_type: data.tnc_type,
      must_acknowledge_terms: data.must_acknowledge_terms,
    },
  };

  if (
    referralProgramIdentifier &&
    referralProgramIdentifier.attribute_details &&
    referralProgramIdentifier.attribute_details.length > 0
  ) {
    payload.referral_program_identifier = referralProgramIdentifier;
  }

  return payload;
};

export const hidePreviewSection = (currentStage: ReferralStage): boolean =>
  ['completed', 'bonus', 'settings', 'jobs', 'user_access'].includes(
    currentStage,
  );

export const isValidForMarkAsConsidered = (
  allowReferralWithoutJob: boolean | null | undefined,
  job_id: string,
): boolean => allowReferralWithoutJob || !!job_id;

export const PROGRAM_TABS = [
  {tabId: 'setup', label: 'Program Details'},
  {tabId: 'questionnaire', label: 'Form'},
  {tabId: 'user_access', label: 'User Access'},
] as const;

export const getProgramJobListTabs = (
  t: (arg1: string, arg2: string) => string,
  referralPayoutEnabled?: boolean,
): Array<{
  tabId: ReferralStage | string;
  label?: string;
}> => [
  {tabId: 'setup', label: t('Program_Details', 'Program Details')},
  {tabId: 'questionnaire', label: t('Form', 'Form')},
  {tabId: 'user_access', label: t('User_Access', 'User Access')},
  ...(referralPayoutEnabled
    ? [{tabId: 'settings', label: t('Bonus_Validity', 'Bonus & Validity')}]
    : []),
  {tabId: 'jobs', label: t('Jobs', 'Jobs')},
];

export const DEFAULT_JOB_LIST_PROGRAM_MAPPING = {
  setup: 1,
  settings: 2,
  questionnaire: 3,
  user_access: 4,
  jobs: 5,
  completed: 6,
} as const;

export const NON_DEFAULT_JOB_LIST_PROGRAM_MAPPING = {
  setup: 1,
  questionnaire: 2,
  user_access: 3,
  settings: 4,
  jobs: 5,
  completed: 6,
} as const;

export const DEFAULT_PROGRAM_MAPPING = {
  setup: 1,
  settings: 2,
  questionnaire: 3,
  user_access: 4,
  completed: 5,
  // jobs step is not required for non default program so assigning it's value as zero
  jobs: 0,
} as const;

export const NON_DEFAULT_PROGRAM_MAPPING = {
  setup: 1,
  questionnaire: 2,
  user_access: 3,
  completed: 4,
  // settings and jobs step is not required for non default program so assigning it's value as zero
  settings: 0,
  jobs: 0,
} as const;

export const getProgramUserRole = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'view', label: t('As_a_Viewer', 'As a Viewer')},
  {key: 'edit', label: t('As_a_Editor', 'As a Editor')},
];

export const getReferralUserGroup = (
  t: (arg1: string, arg2: string) => string,
): {
  [key: string]: {
    label: string;
    enum: string;
  };
} => ({
  viewer: {label: t('Viewer', 'Viewer'), enum: 'viewer'},
  admin: {label: t('Admin', 'Admin'), enum: 'admin'},
  super_admin: {label: t('Super_Admin', 'Super Admin'), enum: 'super_admin'},
});

export const getUserAccessActionOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'no_access', label: t('Remove_Access', 'Remove Access')},
];

export const DEFAULT_PAGE_NUMBER = 1;

export const getProgramFilter = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {
    label: t('Active_Programs', 'Active Programs'),
    key: 'active',
  },
  {
    label: t('Inactive_Programs', 'Inactive Programs'),
    key: 'inactive',
  },
];

export const TRACKING_ACTION_OPTIONS = [
  {label: 'Mark As Considered', value: 'Considered'},
  {label: 'Mark As Hired', value: 'Hired'},
] as const;

export const pathsToCheck = [
  'setup',
  'programs',
  'payouts',
  'tracking',
  'setting',
  'job',
  'create',
  'change-log',
] as const;

export const getTimeSheetCsvRows = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  label: string;
  value: string;
  required: boolean;
}> => [
  {
    label: t('Name', 'Name'),
    value: 'name',
    required: true,
  },
  {
    label: t('Candidate_ID', 'Candidate ID'),
    value: 'candidate id',
    required: true,
  },
  {
    label: t('Job_Order_ID', 'Job Order ID'),
    value: 'job order id',
    required: true,
  },
  {
    label: t('Placement_ID', 'Placement ID'),
    value: 'placement id',
    required: true,
  },
  {
    label: t('Total_Time', 'Total Time'),
    value: 'total time',
    required: true,
  },
];

export const getUploadCsvRows = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  label: string;
  value: string;
  required: boolean;
}> => [
  {
    label: t('Candidate_ID', 'Candidate ID'),
    value: 'candidate id',
    required: true,
  },
  {
    label: t('Candidate_Email', 'Candidate Email'),
    value: 'candidate email',
    required: false,
  },
  {label: t('Job_ID', 'Job ID'), value: 'job id', required: true},
  {
    label: t('Referrer_Name', 'Referrer Name'),
    value: 'referrer name',
    required: true,
  },
  {
    label: t('Referrer_Email', 'Referrer Email'),
    value: 'referrer email',
    required: true,
  },
  {
    label: t('Referral_Date', 'Referral Date'),
    value: 'referral date',
    required: false,
  },
  {
    label: t('Payout_Date', 'Payout Date'),
    value: 'payout date',
    required: false,
  },
];

export const sampleTimeSheetData = [
  {
    Name: 'John',
    'Candidate ID': '789273',
    'Job Order ID': '8989',
    'Placement ID': '78983273',
    'Total Time': '800',
  },
] as const;

export const sampleUploadCsvData = [
  {
    'Candidate ID': '789273',
    'Candidate Email': 'emample@gmail.com',
    'Job ID': '873465',
    'Referrer Name': 'John',
    'Referrer Email': 'john@gmail.com',
    'Referral Date': '01-15-2023',
    'Payout Date': '06-15-2023',
  },
] as const;

export const programLinkCsvRow = [
  'Program ID',
  'Program Name',
  'Program Type',
  'Agent Name',
  'Agency Link',
  'Agent Link',
] as const;

export const INPUT_TEXT_LIMIT = {
  default: 150,
  program_message: 500,
  form_recommendation: 500,
  number: 6,
} as const;

export const bonusAmountLimit = 999999;

export const defaultJobProgramFilter = [
  {
    name: 'All jobs',
    id: '',
  },
] as const;

export const getPaypalSetupInformation = (
  t: (arg1: string, arg2: string) => string,
): Array<{
  id: string;
  text?: string;
  link?: {
    url: string;
    text: string;
  };
}> => [
  {
    id: '1',
    text: `${t(
      'Login_to_you_PayPal_account_and',
      'Login to you PayPal account and',
    )} `,
    link: {
      url: 'https://www.paypal.com/us/digital-wallet/manage-money/add-cash',
      text: t('visit_applications_page', 'visit applications page'),
    },
  },
  {
    id: '2',
    text: `${t('Select_create_app', 'Select create app')}.`,
  },
  {
    id: '3',
    text: `${t(
      'Under_app_type_select_platform',
      'Under app type select platform',
    )} - ${t(
      'Move_payments_to_sellers_as_a_platform',
      'Move payments to sellers as a platform',
    )} (${t(
      'marketplace_crowdfunding_or_ecommerce_platform',
      'marketplace, crowdfunding, or e-commerce platform',
    )}).`,
  },
  {
    id: '4',
    text: `${t('Select_create_app', 'Select create app')}.`,
  },
  {
    id: '5',
    text: `${t('Done', 'Done')}! ${t(
      'The_page_will_display_clients_ID_and_secret_Copy_and_enter_them_above',
      'The page will display clients ID and secret. Copy and enter them above',
    )}.`,
  },
];

export const DEFAULT_TABLE_PAGE_SIZE = 13;

export const DEFAULT_DESKTOP_WINDOW_SIZE = 1170;

export const getNotificationSettingsOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {label: t('Submitted', 'Submitted'), key: 'submission'},
  {label: t('Status_Change', 'Status Change'), key: 'status_change'},
  {label: t('Withdrawn', 'Withdrawn'), key: 'withdrawn'},
  {label: t('Payments', 'Payments'), key: 'payments'},
];

export const getTimeFrameOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {label: t('Last_90_days', 'Last 90 days'), key: '90'},
  {label: t('Last_30_days', 'Last 30 days'), key: '30'},
  {label: t('Last_7_days', 'Last 7 days'), key: '7'},
  {label: t('Custom_Date_Range', 'Custom Date Range'), key: 'date-range'},
];

export const getReasonActionMapping = (
  t: (arg1: string, arg2: string) => string,
): {
  [key: string]: {
    text: string;
    actionText: string;
  };
} => ({
  failed: {
    text: `${t(
      'Reason_for_failure_of_payment_transaction_here',
      'Reason for failure of payment transaction here',
    )}.`,
    actionText: t('Retry', 'Retry'),
  },
  unclaimed: {
    text: `${t('Not_claimed_yet', 'Not claimed yet')}.`,
    actionText: t('Cancel_Transaction', 'Cancel Transaction'),
  },
});

export const getTimeAgoOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'last_year', label: t('Last_year', 'Last year')},
  {key: '90', label: t('Last_90_days', 'Last 90 days')},
  {key: '30', label: t('Last_30_days', 'Last 30 days')},
  {key: 'last_month', label: t('Last_month', 'Last month')},
  {key: '7', label: t('Last_7_days', 'Last 7 days')},
];

export const payoutActionOptions = (
  payment_status: string,
  selected: boolean,
  t: (arg1: string, arg2: string) => string,
): MenuOption[] => {
  return [
    {
      label: t('Edit_Bonus', 'Edit Bonus'),
      key: 'edit_bonus',
      disabled: payment_status === 'Paid' || selected,
    },
    {label: t('Archive', 'Archive'), key: 'archive'},
    {label: t('Edit_Start_Date', 'Edit Start Date'), key: 'edit_start_date'},
    {
      label: t('Mark_as_Paid', 'Mark as Paid'),
      key: 'mark_as_paid',
      disabled: payment_status !== 'Payout Pending' || selected,
    },
    {
      label: t('Pay_using_PayPal', 'Pay using PayPal'),
      key: 'stage_change',
      disabled: payment_status !== 'Payout Pending' || selected,
    },
  ];
};

type Extras = {
  programId: string;
  selectedJobs: string[];
  handleSelectReferralJob: (arg1: string[]) => unknown;
  page: number;
  hasEditPermission: boolean;
};

const PAYOUT_JOB_HEADER_KEYS = [
  'payout_amount',
  'payout_period',
  'referral_validity',
  'action',
] as const;

export const getJobHeaders = (
  t: (arg1: string, arg2: string) => string,
  hidePayoutColumns?: boolean,
): GenericHeaderItems<ReferralJob, Extras> => {
  const allHeaders: GenericHeaderItems<ReferralJob, Extras> = [
    {
      key: 'title',
      label: t('Job_Title', 'Job Title'),
    },
    {
      key: 'owner_name',
      label: t('Job_Owner', 'Job Owner'),
    },
    {
      key: 'location',
      label: t('Location', 'Location'),
    },
    {
      key: 'payout_amount',
      label: t('Bonus_Amount', 'Bonus Amount'),
      sortable: true,
    },
    {
      key: 'payout_period',
      label: `${t('Payout_Period', 'Payout Period')} (${t(
        'in_days',
        'in days',
      )})`,
      sortable: true,
    },
    {
      key: 'referral_validity',
      label: `${t('Referral_Validity', 'Referral Validity')} (${t(
        'in_days',
        'in days',
      )})`,
      sortable: true,
    },
    {key: 'action', label: t('Action', 'Action')},
  ];
  if (hidePayoutColumns) {
    return allHeaders.filter(
      (h) => !PAYOUT_JOB_HEADER_KEYS.includes(h.key as any),
    );
  }
  return allHeaders;
};

export const getOperatorOptions = (
  fieldType: string,
  t: (arg1: string, arg2: string) => string,
): {
  key: string;
  label: string;
  tooltip: string;
}[] => {
  switch (fieldType) {
    case 'boolean':
      return [
        {
          key: 'eq',
          label: t('is', 'is'),
          tooltip: 'isActive = "true/false"',
        },
      ];
    default:
      return [
        {
          key: 'eq',
          label: t('is', 'is'),
          tooltip: t(
            'Define_the_value_which_the_field_is_expected_to_have_in_quotes_eg_placed',
            'Define the value which the field is expected to have in quotes e.g. "placed"',
          ),
        },
        {
          key: 'neq',
          label: t('is_not', 'is not'),
          tooltip: t(
            'Define_the_value_which_the_field_is_not_expected_to_have_in_quotes_eg_placed',
            'Define the value which the field is not expected to have in quotes e.g. "placed"',
          ),
        },
        {
          key: 'is_not_null',
          label: t('is_setpresent', 'is set/present'),
          tooltip: t('Value_not_required', 'Value not required'),
        },
        {
          key: 'is_null',
          label: t('is_not_setpresent', 'is not set/present'),
          tooltip: t('Value_not_required', 'Value not required'),
        },
        {
          key: 'contains',
          label: t('contains_one_of', 'contains one of'),
          tooltip: t(
            'Multiple_values_are_expected_separated_by_comma_eg_hired_confirmed_start_etc',
            'Multiple values are expected separated by comma e.g. "hired", "confirmed start" etc.',
          ),
        },
        {
          key: 'ncontains',
          label: t('does_not_contain_one_of', 'does not contain one of'),
          tooltip: t(
            'Multiple_values_are_expected_separated_by_comma_eg_hired_confirmed_start_etc',
            'Multiple values are expected separated by comma e.g. "hired", "confirmed start" etc.',
          ),
        },
      ];
  }
};

export const entityValueTypeOptions = [
  {label: 'Custom Value', key: 'Custom Value'},
  {label: 'Referral Field', key: 'Referral Field'},
] as const;

export const getBooleanDropdownOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {label: t('True', 'True'), key: 'true'},
  {label: t('False', 'False'), key: 'false'},
];

export const offsetOptions = [
  {label: 'Plus (+)', key: '+'},
  {label: 'Minus (-)', key: '-'},
] as const;

export const getCustomQuestionTypeOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'referral', label: t('Referral_Question', 'Referral Question')},
  {key: 'referrer', label: t('Referrer_Question', 'Referrer Question')},
];

export const JOB_LIST_DATA_LIMIT = 2000;

export const uploadFileStepper = {
  uploadFile: 0,
  mapData: 1,
} as const;

export const getUploadCsvStatusOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {key: 'queued', label: t('Queued', 'Queued')},
  {key: 'processing', label: t('Processing', 'Processing')},
  {key: 'partial_success', label: t('Partial_Success', 'Partial Success')},
  {key: 'success', label: t('Success', 'Success')},
  {key: 'failed', label: t('Failed', 'Failed')},
];

export const ApiErrorText =
  'text: \'An unexpected error occured when attempting to save your changes. If this problem persists, please contact support.\',';

export const highPercentile = 90;
export const midPercentile = 60;
